/**
 * Global Target Finder Modal
 *
 * Cmd+K / Ctrl+K global search across all weaponeering entities.
 * Provides fuzzy search by short-code, name, or business ID.
 * Full keyboard navigation (↑↓ Enter Esc).
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useWeaponeeringSearch } from '../hooks/useWeaponeeringSearch';

const COLORS = {
  background: '#070a0d',
  panel: '#0c1117',
  panelBorder: '#1a2533',
  primary: '#00d4ff',
  success: '#00ff88',
  warning: '#ffaa00',
  danger: '#ff4444',
  text: '#e6f0ff',
  textDim: '#5a7090',
};

// Entity type color coding
const ENTITY_COLORS = {
  site: '#00d4ff', // Blue
  facility: '#a855f7', // Purple
  dmpi: '#fbbf24', // Yellow
};

const ENTITY_ICONS = {
  site: '🏢',
  facility: '🏭',
  dmpi: '⌖',
};

const ENTITY_TYPE_LABELS = {
  site: 'Installation',
  facility: 'Facility',
  dmpi: 'Aim Point',
};

export default function GlobalTargetFinderModal({ isOpen, onClose, onSelectEntity }) {
  const {
    query,
    setQuery,
    results,
    loading,
    error,
    filters,
    updateFilters,
    clearFilters,
  } = useWeaponeeringSearch();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex, results]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleSelectEntity(results[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleSelectEntity = (entity) => {
    if (onSelectEntity) {
      onSelectEntity(entity);
    }
  };

  const getEntityColor = (entityType) => {
    return ENTITY_COLORS[entityType] || COLORS.textDim;
  };

  const getEntityIcon = (entityType) => {
    return ENTITY_ICONS[entityType] || '📍';
  };

  const formatLineage = (result) => {
    const parts = [];
    if (result.site_name) parts.push(result.site_name);
    if (result.facility_name) parts.push(result.facility_name);
    if (result.dmpi_name && result.entity_type === 'dmpi') parts.push(result.dmpi_name);
    return parts.length > 0 ? parts.join(' → ') : result.name;
  };

  const formatCoordinates = (coords) => {
    // Use typeof checks (not truthiness) -- a real result can legitimately sit
    // on the equator or prime meridian (lat/lon === 0), which `!coords.lat`
    // would wrongly treat as "no coordinates" and hide from the UI.
    if (!coords || typeof coords.lat !== 'number' || typeof coords.lon !== 'number') return null;
    const lat = coords.lat.toFixed(4);
    const lon = coords.lon.toFixed(4);
    return `${lat}, ${lon}`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="target-finder-title"
        style={{
          position: 'fixed',
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '700px',
          background: COLORS.panel,
          border: `1px solid ${COLORS.panelBorder}`,
          borderRadius: '8px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${COLORS.panelBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🔍</span>
            <h2
              id="target-finder-title"
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 700,
                color: COLORS.primary,
                letterSpacing: '0.5px',
              }}
            >
              GLOBAL TARGET FINDER
            </h2>
          </div>
          <div style={{ fontSize: '11px', color: COLORS.textDim, fontFamily: 'monospace' }}>
            Cmd+K
          </div>
        </div>

        {/* Search Input */}
        <div style={{ padding: '20px 24px' }}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by short-code, name, or ID..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search for weaponeering entities"
            aria-describedby="search-instructions"
            aria-controls="search-results"
            autoComplete="off"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: COLORS.background,
              border: `1px solid ${COLORS.panelBorder}`,
              borderRadius: '5px',
              color: COLORS.text,
              fontSize: '15px',
              outline: 'none',
            }}
            onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
            onBlur={(e) => (e.target.style.borderColor = COLORS.panelBorder)}
          />

          {/* Keyboard Shortcuts Help */}
          <div
            id="search-instructions"
            style={{
              marginTop: '8px',
              fontSize: '11px',
              color: COLORS.textDim,
              fontFamily: 'monospace',
              display: 'flex',
              gap: '16px',
            }}
          >
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>Esc Close</span>
          </div>

          {/* Filters */}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['site', 'facility', 'dmpi'].map((type) => (
              <button
                key={type}
                onClick={() =>
                  updateFilters({
                    entity_type: filters.entity_type === type ? null : type,
                  })
                }
                style={{
                  padding: '6px 12px',
                  background:
                    filters.entity_type === type ? getEntityColor(type) : COLORS.background,
                  border: `1px solid ${filters.entity_type === type ? getEntityColor(type) : COLORS.panelBorder}`,
                  borderRadius: '4px',
                  color: filters.entity_type === type ? COLORS.background : COLORS.textDim,
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: filters.entity_type === type ? 600 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {getEntityIcon(type)} {ENTITY_TYPE_LABELS[type] || type.toUpperCase()}
              </button>
            ))}

            {filters.entity_type && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: `1px solid ${COLORS.panelBorder}`,
                  borderRadius: '4px',
                  color: COLORS.textDim,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div
          id="search-results"
          ref={resultsRef}
          role="listbox"
          aria-label="Search results"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            borderTop: `1px solid ${COLORS.panelBorder}`,
          }}
        >
          {/* Loading State */}
          {loading && (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
                color: COLORS.textDim,
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div>Searching...</div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
                color: COLORS.danger,
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>❌</div>
              <div>{error}</div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && query && results.length === 0 && (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
                color: COLORS.textDim,
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
              <div>No results found for "{query}"</div>
              <div style={{ fontSize: '12px', marginTop: '8px' }}>
                Try searching by short-code (e.g., SIT-001) or name
              </div>
            </div>
          )}

          {/* Results List */}
          {!loading && !error && results.length > 0 && (
            <>
              {results.map((result, index) => (
                <div
                  key={result.entity_id}
                  onClick={() => handleSelectEntity(result)}
                  role="option"
                  aria-selected={index === selectedIndex}
                  tabIndex={-1}
                  style={{
                    padding: '16px 24px',
                    borderBottom: `1px solid ${COLORS.panelBorder}`,
                    background: index === selectedIndex ? COLORS.background : 'transparent',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = COLORS.background;
                    setSelectedIndex(index);
                  }}
                  onMouseLeave={(e) => {
                    if (index !== selectedIndex) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Entity Icon */}
                    <div style={{ fontSize: '20px' }}>{getEntityIcon(result.entity_type)}</div>

                    {/* Entity Info */}
                    <div style={{ flex: 1 }}>
                      {/* Short Code + Name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {result.short_code && (
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              fontWeight: 700,
                              color: getEntityColor(result.entity_type),
                            }}
                          >
                            {result.short_code}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: '15px',
                            fontWeight: 600,
                            color: COLORS.text,
                          }}
                        >
                          {result.name}
                        </span>
                        {result.ai_suggested && (
                          <span
                            style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              background: COLORS.primary,
                              color: COLORS.background,
                              borderRadius: '3px',
                              fontWeight: 700,
                            }}
                          >
                            AI
                          </span>
                        )}
                      </div>

                      {/* Lineage + Coordinates */}
                      <div
                        style={{
                          fontSize: '12px',
                          color: COLORS.textDim,
                          marginTop: '4px',
                        }}
                      >
                        {formatLineage(result)}
                        {formatCoordinates(result.coordinates) && (
                          <span style={{ marginLeft: '12px', fontFamily: 'monospace' }}>
                            📍 {formatCoordinates(result.coordinates)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Entity Type Badge */}
                    <div
                      style={{
                        padding: '4px 10px',
                        background: `${getEntityColor(result.entity_type)}20`,
                        border: `1px solid ${getEntityColor(result.entity_type)}`,
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: getEntityColor(result.entity_type),
                        textTransform: 'uppercase',
                      }}
                    >
                      {ENTITY_TYPE_LABELS[result.entity_type] || result.entity_type}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: `1px solid ${COLORS.panelBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            color: COLORS.textDim,
            fontFamily: 'monospace',
          }}
        >
          <div>
            {results.length > 0 && (
              <span>
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <span>↑↓ Navigate</span>
            <span>Enter Select</span>
            <span>Esc Close</span>
          </div>
        </div>
      </div>
    </>
  );
}

GlobalTargetFinderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectEntity: PropTypes.func.isRequired,
};
